<template>
  <v-container class="pa-0" v-if="profile">
    <div>
      <h1>Nieuwe afspraak maken</h1>
    </div>
    <v-form
      ref="profileForm"
      v-model="valid"
      @submit.prevent="postAppointment"
      lazy-validation
    >
      <v-card class="appt-card">
        <v-window v-model="step" touchless>
          <v-window-item :value="1">
            <v-card-text>
              <h3 class="appt-heading title font-weight-light mb-2">
                Selecteer zorglocatie(s)
              </h3>
            </v-card-text>
            <v-list class="pa-0 no-background-color my-profile-list">
              <v-list-tile
                class="location-list"
                v-for="location in locations"
                :key="location.id"
              >
                <v-list-tile-action>
                  <v-checkbox
                    v-model="locationsSelected"
                    multiple
                    :value="location"
                  />
                </v-list-tile-action>
                <v-list-tile-content
                  :class="
                    location.id === profile.practice.id ? 'my-location' : null
                  "
                >
                  <v-list-tile-title>{{ location.name }}</v-list-tile-title>
                  <span v-show="location.id === profile.practice.id"
                    >(Jouw locatie)</span
                  >
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-window-item>

          <v-window-item :value="2">
            <v-card-text>
              <h3 class="appt-heading title font-weight-light mb-2">
                Selecteer zorgkamer(s)
              </h3>
              <p v-if="!locationsSelected">
                Er zijn nog geen locaties geselecteerd.
              </p>
            </v-card-text>
            <v-list class="pa-0 no-background-color my-profile-list">
              <v-list-tile
                class="location-list"
                v-for="room in getLocationsRooms"
                :key="`room-${room.id}`"
              >
                <v-list-tile-action>
                  <v-checkbox
                    v-model="roomsSelected"
                    multiple
                    :value="room"
                  />
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title>
                    {{ room.title }} - {{ room.practice.name }}
                  </v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-window-item>

          <v-window-item :value="3">
            <v-card-text>
              <h3 class="appt-heading title font-weight-light mb-2">
                Selecteer eventueel materiaal
              </h3>
            </v-card-text>
            <v-list class="pa-0 no-background-color my-profile-list">
              <v-list-tile
                class="location-list"
                v-for="material in getRoomsMaterials"
                :key="material.id"
              >
                <v-list-tile-action>
                  <v-checkbox
                    v-model="materialsSelected"
                    multiple
                    :value="material"
                  />
                </v-list-tile-action>
                <v-list-tile-content>
                  <v-list-tile-title>{{ material.title }}</v-list-tile-title>
                </v-list-tile-content>
              </v-list-tile>
            </v-list>
          </v-window-item>

          <v-window-item :value="4">
            <v-card-text>
              <h3 class="appt-heading title font-weight-light mb-2">
                Selecteer eventueel een datum vanaf, tijd vanaf en tijdsduur
              </h3>
            </v-card-text>
            <v-dialog
              ref="datepickerdialog"
              class="datepickerdialog"
              v-model="showDatepicker"
              :return-value.sync="startDate"
              persistent
              lazy
              full-width
              width="290px"
            >
              <template v-slot:activator="{ on }">
                <v-text-field
                  v-model="startDate"
                  label="Datum vanaf"
                  readonly
                  :rules="dateRules"
                  required
                  v-on="on"
                ></v-text-field>
              </template>
              <v-date-picker
                v-model="startDate"
                scrollable
                locale="nl-nl"
                :min="now"
              >
                <v-spacer></v-spacer>
                <v-btn flat color="primary" @click="showDatepicker = false"
                  >Annuleren
                </v-btn>
                <v-btn
                  flat
                  color="primary"
                  @click="$refs.datepickerdialog.save(startDate)"
                  >OK
                </v-btn>
              </v-date-picker>
            </v-dialog>
            <v-select v-model="startBlockId" :items="blocks" label="Tijd vanaf">
            </v-select>
            <v-select
              v-model="numberOfBlocks"
              :items="numberOfBlocksItems"
              label="Tijdsduur"
            >
            </v-select>
          </v-window-item>

          <v-window-item :value="5" v-show="suggestions">
            <v-card-text>
              <h3
                v-if="suggestions.status == 'error'"
                class="appt-heading title font-weight-light mb-2"
              >
                {{ suggestions.message }}
              </h3>
              <h3 v-else class="appt-heading title font-weight-light mb-2">
                Selecteer een suggestie:
              </h3>
            </v-card-text>
            <div v-if="suggestions.status == 'ok'" class="appointments">
              <div
                class="appointment"
                :key="`suggestion-${i}`"
                v-for="(suggestion, i) in suggestions.suggestions"
                @click="
                  selectedSuggestion = suggestion;
                  step++;
                "
              >
                <p class="appt-date">
                  {{ dateFormatFriendly(suggestion.date) }}
                  {{ suggestion.blockName }}
                </p>
                <p class="location">
                  {{ suggestion.practiceName }} - {{ suggestion.roomName }}
                </p>
                <ul>
                  <li
                      :key="`material-suggestion-${material.name}`"
                      v-for="material in suggestion.roomMaterialNames"
                  >
                    {{ material.title }}
                    {{ material.inRoom ? "(in kamer)" : "(algemeen)" }}
                  </li>
                </ul>

              </div>
            </div>
          </v-window-item>

          <v-window-item :value="6">
            <div class="appointments" v-if="selectedSuggestion != undefined">
              <h3 class="appt-heading title font-weight-light mb-2">
                {{ dateFormatFriendly(selectedSuggestion.date) }}
                {{ selectedSuggestion.blockName }}<br />
                {{ selectedSuggestion.practiceName }} -
                {{ selectedSuggestion.roomName }}
              </h3>
              <div v-if="selectedSuggestion.roomMaterialIds">
                <p>Gekozen materialen:</p>
                <ul>
                  <li
                    :key="`material-${material.name}`"
                    v-for="material in selectedSuggestion.roomMaterialNames"
                  >
                    {{ material.title }}
                    {{ material.inRoom ? "(in kamer)" : "(algemeen)" }}
                  </li>
                </ul>
                <br>
              </div>
              <p>E-mailadres</p>
              <v-text-field dense outlined v-model="emailClient"></v-text-field>
              <v-checkbox
                v-model="sendEmail"
                :label="
                  `Mail een overzicht van de gemaakte afspraken naar de behandelaar en eventueel naar het hierboven opgegeven adres.`
                "
              ></v-checkbox>

              <h5>Opmerkingen</h5>
              <v-textarea
                outlined
                clear-icon="mdi-close-circle"
                name="input-7-4"
                v-model="comment"
              ></v-textarea>
              <span>Achtereenvolgens inplannen:</span>
              <div class="d-flex">
                <v-select
                  :items="[
                    { id: 1, name: '1' },
                    { id: 2, name: '2' },
                    { id: 3, name: '3' },
                    { id: 4, name: '4' },
                    { id: 5, name: '5' },
                    { id: 6, name: '6' },
                    { id: 7, name: '7' },
                    { id: 8, name: '8' },
                    { id: 9, name: '9' },
                    { id: 10, name: '10' },
                    { id: 11, name: '11' },
                    { id: 12, name: '12' },
                    { id: 13, name: '13' },
                    { id: 14, name: '14' },
                    { id: 15, name: '15' },
                    { id: 16, name: '16' },
                    { id: 17, name: '17' },
                    { id: 18, name: '18' },
                    { id: 19, name: '19' },
                    { id: 20, name: '20' }
                  ]"
                  item-value="id"
                  item-text="name"
                  v-model="repeatAmount"
                  dense
                  outlined
                ></v-select>
                <v-select
                  :items="[
                    { id: 'days', name: 'dag/dagen' },
                    { id: 'weeks', name: 'week/weken' }
                  ]"
                  item-value="id"
                  item-text="name"
                  v-model="repeatType"
                  dense
                  outlined
                ></v-select>
              </div>
            </div>
          </v-window-item>

          <v-window-item :value="7">
            <div class="appointments">
              <h3
                v-if="createAppointmentMessage"
                class="appt-heading title font-weight-light mb-2"
              >
                {{ createAppointmentMessage }}
              </h3>
            </div>
          </v-window-item>
        </v-window>

        <v-card-actions class="appt-actions justify-space-around">
          <v-btn
            class="btn-next"
            v-if="step === 1"
            v-show="this.locationsSelected.length"
            depressed
            @click="step++"
          >
            Volgende
          </v-btn>

          <v-btn
            class="btn-next"
            v-if="step === 2"
            v-show="this.roomsSelected.length"
            depressed
            @click="step++"
          >
            Volgende
          </v-btn>

          <v-btn class="btn-next" v-if="step === 3" depressed @click="step++">
            Volgende
          </v-btn>

          <v-btn
            class="btn-next"
            v-if="step === 4"
            depressed
            @click="
              postAppointment();
              step++;
            "
          >
            Volgende
          </v-btn>

          <v-btn
            class="btn-confirm"
            v-if="step === 6"
            depressed
            @click="
              createAppointment();
              step++;
            "
            color="light-green"
          >
            Afspraken aanmaken
          </v-btn>

          <v-btn
            class="btn-confirm"
            v-if="step === 7"
            depressed
            @click="$router.push({ name: 'my-appointments' })"
            color="light-green"
          >
            Bekijk alle afspraken en reserveringen
          </v-btn>

          <v-btn
            class="btn-prev"
            v-show="step === 1"
            text
            depressed
            @click.stop="$router.push({ name: 'my-appointments' })"
          >
            Terug
          </v-btn>

          <v-btn
            class="btn-prev"
            v-show="step !== 1"
            :disabled="step === 1"
            text
            depressed
            @click="step--"
          >
            Vorige
          </v-btn>

        </v-card-actions>
      </v-card>
    </v-form>
  </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

export default {
  created() {
    this.fetchProfile();
    this.fetchAllLocations(), this.fetchAllRooms(), this.fetchAllMaterials();
  },
  computed: {
    ...mapGetters("appointment", [
      "getLocation",
      "getRooms",
      "getMaterials",
      "getSuggestions"
    ]),
    ...mapGetters("user", ["getProfile"]),
    now: function() {
      return moment().format("YYYY-MM-DD");
    },
    getLocationsRooms() {
      return this.rooms.filter(room => {
        return this.locationsSelected.filter(location => location.id === room.practice.id).length
      });
    },
    getRoomsMaterials() {
      return this.materials.filter(material => {
        return this.locationsSelected.filter(location => {
          return material.material_rooms.filter(material_room => material_room.practice_id === location.id).length
        }).length;
      });
    }
  },
  data: () => ({
    profile: null,
    step: 1,
    locations: [],
    rooms: [],
    materials: [],
    suggestions: [],
    error: "",
    createAppointmentMessage: "",
    selectedSuggestion: undefined,
    comment: "",
    emailClient: "",
    sendEmail: "",
    repeatAmount: 1,
    repeatType: "days",
    valid: true,
    locationsSelected: [],
    roomsSelected: [],
    materialsSelected: [],
    startDate: "",
    startBlockId: 1,
    numberOfBlocks: 2,
    dateRules: [v => !!v || "Datum is verplicht"],
    showDatepicker: false,
    blocks: [
      { value: 1, text: "08:30" },
      { value: 2, text: "09:00" },
      { value: 3, text: "09:30" },
      { value: 4, text: "10:00" },
      { value: 5, text: "10:30" },
      { value: 6, text: "11:00" },
      { value: 7, text: "11:30" },
      { value: 8, text: "12:00" },
      { value: 9, text: "12:30" },
      { value: 10, text: "13:00" },
      { value: 11, text: "13:30" },
      { value: 12, text: "14:00" },
      { value: 13, text: "14:30" },
      { value: 14, text: "15:00" },
      { value: 15, text: "15:30" },
      { value: 16, text: "16:00" },
      { value: 17, text: "16:30" },
      { value: 18, text: "17:00" }
    ],
    numberOfBlocksItems: [
      { value: 1, text: "Half uur" },
      { value: 2, text: "1 uur" },
      { value: 3, text: "1,5 uur" },
      { value: 4, text: "2 uur" },
      { value: 5, text: "2,5 uur" },
      { value: 6, text: "3 uur" },
      { value: 7, text: "3,5 uur" },
      { value: 8, text: "4 uur" },
      { value: 9, text: "4,5 uur" },
      { value: 10, text: "5 uur" },
      { value: 11, text: "5,5 uur" },
      { value: 12, text: "6 uur" },
      { value: 13, text: "6,5 uur" },
      { value: 14, text: "7 uur" },
      { value: 15, text: "7,5 uur" },
      { value: 16, text: "8 uur" },
      { value: 17, text: "de hele dag" }
    ]
  }),
  methods: {
    dateFormatFriendly(d) {
      return window.dateFormatFriendly(d);
    },
    fetchProfile() {
      this.$store.dispatch("user/fetchProfile").then(() => {
        this.profile = this.getProfile;
      });
    },
    fetchAllLocations() {
      this.$store.dispatch("appointment/fetchLocations").then(() => {
        this.locations = this.getLocation();
      });
    },
    fetchAllRooms() {
      this.$store.dispatch("appointment/fetchRooms").then(() => {
        this.rooms = this.getRooms();
      });
    },
    fetchAllMaterials() {
      this.$store.dispatch("appointment/fetchMaterials").then(() => {
        this.materials = this.getMaterials();
      });
    },
    postAppointment() {
      let roomIds = this.roomsSelected.flatMap(value => {
        return value.id;
      });
      let materialIds = this.materialsSelected
        ? this.materialsSelected.flatMap(value => {
            return value.id;
          })
        : [];
      this.$store
        .dispatch("appointment/searchAppointmentSuggestions", {
          source: "app",
          startDate: this.startDate,
          startBlockId: this.startBlockId,
          endBlockId: 17,
          numberOfBlocks: this.numberOfBlocks,
          roomIds: roomIds,
          materialIds: materialIds,
          numberOfSuggestions: 6,
          maxDays: 60
        })
        .then(() => {
          this.suggestions = this.getSuggestions();
        })
        .catch(error => console.log(error));
    },
    createAppointment() {
      this.$store
        .dispatch("appointment/createAppointment", {
          suggestion: JSON.stringify(this.selectedSuggestion),
          repeat_amount: this.repeatAmount,
          repeat_type: this.repeatType,
          email: this.emailClient,
          send_email: this.sendEmail,
          comment: this.comment
        })
        .then(response => {
          this.createAppointmentMessage = response.data.message;
        });
    }
  }
};
</script>

<style scoped>
h1 {
  padding-left: 16px;
}

.appt-heading {
  font-weight: 800 !important;
  color: #03a6ec;
  font-size: 16px !important;
  padding-top: 20px;
}

.btn-prev {
  background: #c0c0c0;
  border: 1px lightgray solid;
  margin-left: 0px !important;
  color: #000;
  font-weight: 800 !important;
  margin-bottom: 15px !important;
  text-transform: none !important;
  width: 100%;
}

.btn-next {
  background: #4caf50 !important;
  margin-left: 0px !important;
  color: #fff !important;
  font-weight: 800 !important;
  margin-bottom: 15px !important;
  text-transform: none !important;
  width: 100%;
}

.btn-confirm {
  background: #4caf50 !important;
  margin-left: 0px !important;
  color: #fff !important;
  font-weight: 800 !important;
  margin-bottom: 15px !important;
  text-transform: none !important;
  width: 100%;
}

.appt-card,
.appt-actions {
  box-shadow: none;
  background-color: #fafafa !important;
}

.my-profile-list .v-list__tile__title {
  font-size: 15px !important;
}

.location-list {
  max-width: 500px;
}

select {
  border: 1px solid gray;
  border-radius: 5px;
  padding: 5px;
}

.appointments {
  display: flex;
  flex-direction: column;
  margin-left: 16px;
}

.appointment {
  max-width: 100%;
  height: 100%;
  background-color: #fff;
  border: 1px solid #d1d0d0;
  color: rgba(0, 0, 0, 0.87);
  margin: 20px 15px 0 0;
  padding: 10px;
  color: #01437a;
  cursor: pointer;
}

.appt-date {
  font-weight: 700;
}
</style>
